import { useMemo, useRef, useState } from "react";

import clsx from "clsx";
import FuzzySearch from "fuzzy-search";
import { groupBy, orderBy } from "lodash";

import { useSalesBreakdownConfigs } from "@components/data/sales-breakdown-configs";
import { GenericTooltip } from "@components/tooltip-container";
import { useUser } from "@components/user-context";
import { usePrettyLogEventPage } from "@components/v2/analytics";
import {
  getExcludedFrequentlyUsedMetricsV2,
  getFrequentlyUsedMetricsV2,
} from "@features/overview-v2/utils";
import { FilterButtonActive } from "@features/sales-v3/top-level-filters/components/filter-button-active";
import { useFeatureFlags } from "@hooks/http";
import {
  type CompareDateRange,
  type IMCAttributionWindowDays,
  type INBAccountingMode,
} from "@north-beam/nb-common";
import { AttributionWindowSelect } from "@pages/sales/report-body-control/attribution-window-select";
import { NbAccountingModeSelect } from "@pages/sales/report-body-control/nb-accounting-mode-select";
import {
  PrimaryButton,
  SecondaryButton,
  TertiaryButton,
} from "@shared/buttons";
import { Modal } from "@shared/modal";
import {
  AttributionModelSelect,
  SelectWithGroupedOptions,
} from "@shared/selects";
import { TextInput } from "@shared/text-input";

import { useMetricsExplorerContext, useQuickFilterOptions } from "../hooks";
import { type TileFilter, type TileFilterQuery } from "../types";

import { AdvancedSearchEditor } from "./advanced-search-editor";
import { CompareDateSelect } from "./compare-date-select";
import { FilterButtonSelect } from "./filter-button-select";
import { FilterQueryInput } from "./filter-query-input";
import { QuickFilterButton } from "./quick-filter-button";

interface Props {
  isOpen: boolean;
  onClose(): void;
  onSubmit(value: {
    name: string | null;
    metricId: string;
    attributionModel: string | null;
    attributionWindow: IMCAttributionWindowDays | null;
    accountingMode: INBAccountingMode | null;
    compareDateRange: CompareDateRange | null;
    filterQuery: TileFilterQuery | null;
    filters: TileFilter[];
    advancedSearch: any | null;
  }): void;
  onDuplicate?(): void;
  onDelete?(): void;
  initialName?: string;
  initialMetricId?: string;
  initialAttributionModel?: string;
  initialAttributionWindow?: IMCAttributionWindowDays;
  initialAccountingMode?: INBAccountingMode;
  initialCompareDateRange?: CompareDateRange;
  initialFilterQuery?: TileFilterQuery;
  initialFilters?: TileFilter[];
  initialAdvancedSearch?: any;
}

export const TileModal = ({
  isOpen,
  onClose,
  onSubmit,
  onDuplicate,
  onDelete,
  initialName,
  initialMetricId,
  initialAttributionModel,
  initialAttributionWindow,
  initialAccountingMode,
  initialCompareDateRange,
  initialFilterQuery,
  initialFilters,
  initialAdvancedSearch,
}: Props) => {
  const prettyLogEventPage = usePrettyLogEventPage();
  const { user } = useUser();
  const { hasFeatureFlag } = useFeatureFlags();
  const { value: breakdownConfigs } = useSalesBreakdownConfigs();
  const {
    filters: {
      attributionModel: topLevelAttributionModel,
      attributionWindow: topLevelAttributionWindow,
      accountingMode: topLevelAccountingMode,
      dateRangeInterval,
      timeGranularity,
    },
    buildMetrics,
  } = useMetricsExplorerContext();

  const nameInputRef = useRef<HTMLInputElement | null>(null);

  const [name, setName] = useState(initialName ?? "");
  const [metricId, setMetricId] = useState<string | null>(
    initialMetricId ?? null,
  );
  const [attributionModel, setAttributionModel] = useState(
    initialAttributionModel ?? null,
  );
  const [attributionWindow, setAttributionWindow] =
    useState<IMCAttributionWindowDays | null>(initialAttributionWindow ?? null);
  const [accountingMode, setAccountingMode] =
    useState<INBAccountingMode | null>(initialAccountingMode ?? null);
  const [compareDateRange, setCompareDateRange] =
    useState<CompareDateRange | null>(initialCompareDateRange ?? null);
  const [filterQuery, setFilterQuery] = useState<TileFilterQuery | null>(
    initialFilterQuery ?? null,
  );
  const [filters, setFilters] = useState<TileFilter[]>(initialFilters ?? []);
  const [advancedSearch, setAdvancedSearch] = useState<any>(
    initialAdvancedSearch ?? null,
  );

  const [isAdvancedExpanded, setIsAdvancedExpanded] = useState(false);
  const [isNameEditing, setIsNameEditing] = useState(false);
  const [aboutToDelete, setAboutToDelete] = useState(false);
  const [isAdvancedSearch, setIsAdvancedSearch] = useState(
    initialAdvancedSearch ? true : false,
  );

  const pageContext = {
    name,
    metricId,
    attributionModel,
    attributionWindow,
    accountingMode,
    compareDateRange,
    filterQuery,
    filters,
  };

  const metrics = useMemo(() => {
    return buildMetrics({
      attributionModel: attributionModel ?? topLevelAttributionModel,
      attributionWindow: attributionWindow ?? topLevelAttributionWindow,
      accountingMode: accountingMode ?? topLevelAccountingMode,
    }).filter((e) => hasFeatureFlag("enableDashboardV2") || !e.isDashboardV2);
  }, [
    buildMetrics,
    topLevelAttributionModel,
    topLevelAttributionWindow,
    topLevelAccountingMode,
    attributionModel,
    attributionWindow,
    accountingMode,
  ]);

  const frequentlyUsedMetrics = getFrequentlyUsedMetricsV2(metrics);
  const otherMetrics = orderBy(
    getExcludedFrequentlyUsedMetricsV2(metrics),
    "name",
  );

  const topLevelFilters = useMemo(() => {
    const grouped = groupBy(filters, "breakdown");
    return Object.entries(grouped).map(([breakdown, values]) => ({
      key: breakdown,
      values: values.map((e) => e.value),
    }));
  }, [filters]);

  const metric = useMemo(
    () => metrics.find(({ id }) => id === metricId),
    [metrics, metricId],
  );

  const quickFilterOptions = useQuickFilterOptions();

  const visibleQuickFilterOptions = quickFilterOptions.filter(
    (e) =>
      !filters.find(
        (filter) =>
          filter.breakdown === e.breakdown && filter.value === e.value,
      ),
  );

  const isAttributionWindowVisible =
    (accountingMode && accountingMode === "accrual") ||
    (!accountingMode && topLevelAccountingMode === "accrual");

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      titleContent={
        <div>
          {isNameEditing ? (
            <div>
              <TextInput
                ref={nameInputRef}
                placeholder="Custom name..."
                inputClassName="w-[400px]"
                value={name}
                onChange={(e) => {
                  prettyLogEventPage({
                    domain: "Metrics Explorer",
                    action: "onChange",
                    additionalActionInfo: "Tile Modal Set name",
                    data: {
                      newName: e.target.value,
                      pageContext,
                    },
                  });

                  setName(e.target.value);
                }}
                onBlur={() => setIsNameEditing(false)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    nameInputRef.current?.blur();
                  }

                  if (e.key === "Escape") {
                    e.stopPropagation();
                    nameInputRef.current?.blur();
                  }
                }}
              />
            </div>
          ) : (
            <div
              className={clsx(
                "font-bold text-xl flex items-end gap-1 cursor-text h-8 w-[400px]",
                { "text-gray-1": !name },
              )}
              onClick={() => {
                setIsNameEditing(true);
                setTimeout(() => {
                  nameInputRef.current?.focus();
                }, 100);
              }}
            >
              <GenericTooltip
                className="truncate"
                noInfoCircle
                mouseEnterDelay={1000}
                placement="top"
                content={name}
              >
                <span className="truncate">{name || "Custom name..."}</span>
              </GenericTooltip>
              <i className={clsx("fa-regular fa-pen text-sm mb-0.5")} />
            </div>
          )}
        </div>
      }
    >
      <div className="mx-0.5 -mt-1 space-y-4">
        <div>
          <label className="mb-1.5 font-bold text-[11px]">
            FILTERS (OPTIONAL)
          </label>
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              {visibleQuickFilterOptions.length > 0 && (
                <div className="flex items-center gap-2">
                  {visibleQuickFilterOptions.map((e) => (
                    <QuickFilterButton
                      key={e.value}
                      filter={e}
                      onClick={() => {
                        const newFilters = [...filters, { ...e }];

                        prettyLogEventPage({
                          domain: "Metrics Explorer",
                          action: "onChange",
                          additionalActionInfo: "Tile Modal Set filters",
                          data: {
                            newFilters,
                            pageContext,
                          },
                        });

                        setFilters(newFilters);
                      }}
                    />
                  ))}
                </div>
              )}
              <FilterButtonSelect
                breakdownConfigs={breakdownConfigs}
                breakdowns={topLevelFilters}
                onChange={(values) => {
                  const newFilters = values.flatMap((breakdown) =>
                    breakdown.values.map((value) => ({
                      breakdown: breakdown.key,
                      value,
                    })),
                  );

                  prettyLogEventPage({
                    domain: "Metrics Explorer",
                    action: "onChange",
                    additionalActionInfo: "Tile Modal Set filters",
                    data: {
                      newFilters,
                      pageContext,
                    },
                  });

                  setFilters(newFilters);
                }}
              />
            </div>
            {topLevelFilters.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 text-xs">
                {topLevelFilters.map((e) => (
                  <FilterButtonActive
                    key={e.key}
                    breakdown={e}
                    tooltipPlacement="left"
                    onDelete={() => {
                      const newFilters = filters.filter(
                        (v) => v.breakdown !== e.key,
                      );

                      prettyLogEventPage({
                        domain: "Metrics Explorer",
                        action: "onChange",
                        additionalActionInfo: "Tile Modal Set filters",
                        data: {
                          newFilters,
                          pageContext,
                        },
                      });

                      setFilters(newFilters);
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
        <div>
          <SelectWithGroupedOptions
            label={<span className="font-bold text-[11px]">METRIC</span>}
            className={clsx(
              "focus:outline-none focus:ring-cosmic focus:border-cosmic focus:ring-1",
              "focus:ring-inset focus-within:outline-none focus-within:ring-inset",
            )}
            menuClassName="z-[9998] max-h-[20rem]"
            CustomSearchClass={FuzzySearch}
            onChange={(metric) => {
              prettyLogEventPage({
                domain: "Metrics Explorer",
                action: "onChange",
                additionalActionInfo: "Tile Modal Change metric",
                data: {
                  newMetricId: metric?.id,
                  pageContext,
                },
              });

              if (metric) {
                setMetricId(metric.id);

                if (metric.isCustom) {
                  setAccountingMode(metric.customAccountingMode ?? null);
                }

                if (metric.isForecast) {
                  setAttributionModel("northbeam_custom");
                  setAccountingMode("accrual");
                }
              } else {
                setMetricId(null);
              }
            }}
            isSearchable
            value={metric}
            groupedOptions={[
              {
                label: "Frequently Used",
                options: frequentlyUsedMetrics.map((metric) => ({
                  label: metric.name,
                  value: metric,
                })),
              },
              {
                options: otherMetrics.map((metric) => ({
                  label: metric.name,
                  value: metric,
                })),
              },
            ]}
          />
        </div>
        <div className="py-1">
          <button
            className="flex items-center gap-2 text-sm"
            onClick={() => setIsAdvancedExpanded((prev) => !prev)}
          >
            Advanced
            <i
              className={clsx("fa-solid", {
                "fa-chevron-down": !isAdvancedExpanded,
                "fa-chevron-up": isAdvancedExpanded,
              })}
            />
          </button>
        </div>
        {isAdvancedExpanded && (
          <div className="space-y-3">
            <div>
              <AttributionModelSelect
                isClearable
                isDisabled={metric?.isForecast}
                label={
                  <span className="font-bold text-[11px]">
                    ATTRIBUTION MODEL
                  </span>
                }
                attributionModel={attributionModel}
                onChange={(value) => {
                  prettyLogEventPage({
                    domain: "Metrics Explorer",
                    action: "onChange",
                    additionalActionInfo: "Tile Modal Set Attribution model",
                    data: {
                      newAttributionModel: value,
                      pageContext,
                    },
                  });

                  setAttributionModel(value);
                }}
              />
              {metric?.isForecast && (
                <div className="text-xxs text-muted mt-1">
                  Attribution model can't be changed for forecasted metric
                </div>
              )}
            </div>
            <div>
              <NbAccountingModeSelect
                label={
                  <span className="font-bold text-[11px]">ACCOUNTING MODE</span>
                }
                nbAccountingMode={accountingMode}
                isLoading={false}
                isClearable
                isDisabled={metric?.isCustom || metric?.isForecast}
                icon={null}
                onChange={(value) => {
                  prettyLogEventPage({
                    domain: "Metrics Explorer",
                    action: "onChange",
                    additionalActionInfo: "Tile Modal Set Accounting mode",
                    data: {
                      newAccountingMode: value,
                      pageContext,
                    },
                  });

                  const newMetrics = buildMetrics({
                    attributionModel:
                      attributionModel ?? topLevelAttributionModel,
                    attributionWindow:
                      attributionWindow ?? topLevelAttributionWindow,
                    accountingMode: value ?? topLevelAccountingMode,
                  });

                  setAccountingMode(value);

                  if (
                    metricId &&
                    !newMetrics.map((e) => e.id).includes(metricId)
                  ) {
                    setMetricId(null);
                  }

                  if (value !== "accrual") {
                    setAttributionWindow(null);
                  }
                }}
              />
              {metric?.isCustom && (
                <div className="text-xxs text-muted mt-1">
                  Accounting mode can't be changed for custom metric
                </div>
              )}
              {metric?.isForecast && (
                <div className="text-xxs text-muted mt-1">
                  Accounting mode can't be changed for forecasted metric
                </div>
              )}
            </div>
            {isAttributionWindowVisible && (
              <div>
                <AttributionWindowSelect
                  label={
                    <span className="font-bold text-[11px]">
                      ATTRIBUTION WINDOW
                    </span>
                  }
                  attributionModel={attributionModel}
                  attributionWindow={attributionWindow}
                  timeGranularity="daily"
                  isClearable
                  onChange={(value) => {
                    prettyLogEventPage({
                      domain: "Metrics Explorer",
                      action: "onChange",
                      additionalActionInfo: "Tile Modal Set Attribution window",
                      data: {
                        newAttributionWindow: value,
                        pageContext,
                      },
                    });

                    setAttributionWindow(value);
                  }}
                />
              </div>
            )}
            <div>
              <CompareDateSelect
                timeGranularity={timeGranularity}
                dateRangeInterval={dateRangeInterval}
                compareDateRange={compareDateRange}
                timezone={user.timezone}
                onCompareDateRangeChange={(value) => {
                  prettyLogEventPage({
                    domain: "Metrics Explorer",
                    action: "onChange",
                    additionalActionInfo: "Tile Modal Set Compare date range",
                    data: {
                      newCompareDateRange: value,
                      pageContext,
                    },
                  });

                  setCompareDateRange(value);
                }}
              />
            </div>
            <div>
              {isAdvancedSearch ? (
                <AdvancedSearchEditor
                  queryBuilderClassName="tile-modal-query-builder"
                  value={advancedSearch}
                  onChange={(value) => {
                    prettyLogEventPage({
                      domain: "Metrics Explorer",
                      action: "onChange",
                      additionalActionInfo: "Tile Modal Set Advanced search",
                      data: {
                        pageContext,
                      },
                    });

                    setAdvancedSearch(value);
                  }}
                  onSwitchToSimpleSearch={() => {
                    prettyLogEventPage({
                      domain: "Metrics Explorer",
                      action: "onChange",
                      additionalActionInfo:
                        "Tile Modal Switch to simple search",
                      data: {
                        pageContext,
                      },
                    });

                    setIsAdvancedSearch(false);
                    setAdvancedSearch(null);
                  }}
                />
              ) : (
                <FilterQueryInput
                  dropdownClassName="z-[2002]"
                  label={
                    <div className="flex items-center justify-between mb-1">
                      <label className="label font-bold text-[11px] mb-0">
                        SEARCH
                      </label>
                      <TertiaryButton
                        className={clsx(
                          "m-0 p-0 leading-3 bg-v2-primary flex items-center gap-1",
                          "group hover:text-cosmic+2",
                        )}
                        onClick={() => {
                          prettyLogEventPage({
                            domain: "Metrics Explorer",
                            action: "onChange",
                            additionalActionInfo:
                              "Tile Modal Switch to advanced search",
                            data: {
                              pageContext,
                            },
                          });

                          setIsAdvancedSearch(true);
                          setFilterQuery(null);
                        }}
                      >
                        <i className="fa-regular fa-gears text-xxs group-hover:text-cosmic+2" />
                        Advanced search
                      </TertiaryButton>
                    </div>
                  }
                  value={filterQuery}
                  onChange={(value) => {
                    prettyLogEventPage({
                      domain: "Metrics Explorer",
                      action: "onChange",
                      additionalActionInfo: "Tile Modal Filter query",
                      data: {
                        newFilterQuery: value,
                        pageContext,
                      },
                    });

                    setFilterQuery(value);
                  }}
                />
              )}
            </div>
          </div>
        )}
        <div className="border-b border-gray-1"></div>
        {aboutToDelete ? (
          <div
            className={clsx(
              "bg-mars-2 border border-mars-1 rounded-lg py-[9px] px-4",
              "flex items-center justify-between mt-[-18px]",
            )}
          >
            <h3 className="text-sm font-bold mb-0 relative">Delete metric?</h3>
            <div className="flex items-center gap-2">
              <PrimaryButton
                className="font-bold text-xs bg-mars"
                onClick={() => {
                  onDelete?.();
                }}
              >
                YES, DELETE
              </PrimaryButton>
              <SecondaryButton
                className="font-bold text-xs"
                onClick={() => setAboutToDelete(false)}
              >
                NO, CANCEL
              </SecondaryButton>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {initialMetricId && (
                <>
                  <TertiaryButton
                    className="font-bold flex items-center gap-2"
                    onClick={() => setAboutToDelete(true)}
                  >
                    <i className={clsx("fa-regular fa-trash")} />
                    DELETE
                  </TertiaryButton>
                  <TertiaryButton
                    className="font-bold flex items-center gap-2"
                    onClick={() => onDuplicate?.()}
                  >
                    <i className={clsx("fa-regular fa-clone")} />
                    DUPLICATE
                  </TertiaryButton>
                </>
              )}
            </div>
            <div className="flex items-center justify-end gap-2">
              <TertiaryButton className="font-bold" onClick={() => onClose()}>
                CANCEL
              </TertiaryButton>
              <PrimaryButton
                className="font-bold flex items-center gap-2"
                disabled={!metricId}
                onClick={() => {
                  if (metricId) {
                    onSubmit({
                      name: name === "" ? null : name,
                      metricId,
                      attributionModel,
                      attributionWindow,
                      accountingMode,
                      compareDateRange,
                      filterQuery,
                      filters,
                      advancedSearch,
                    });
                  }
                }}
              >
                {initialMetricId ? "UPDATE" : "ADD TO DASHBOARD"}
              </PrimaryButton>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};
